<template>
  <div>
    <button v-if="showAddParticipantPlusButton && props.activityOccurrenceId"
      class="btn btn--border util--ml-auto" type="button"
      @click="showAddParticipantPlusButton = !showAddParticipantPlusButton">
      {{t("participants.add")}}
      <span class="plus-sign"></span>
    </button>
    <form 
      class="form" 
      novalidate 
      @submit.prevent="handleSubmit" 
      enctype="multipart/form-data">
      <FormErrorsList 
        v-if="errorsMessages.length" 
        :title="t('global.formErrorNotification')"
        :errorsList="errorsMessages" />

      <Fieldset :title="t('participants.newParticipant')" v-if="!showAddParticipantPlusButton">
        <FormRow>
          <FormInput 
            :ref="addFormInputRef" 
            v-model="newParticipant.firstName" 
            name="newParticipantFirstName"
            type="text" 
            :label="t('participants.firstName')" 
            @validated="handleValidation" />

          <FormInput 
            :ref="addFormInputRef" 
            v-model="newParticipant.lastName" 
            name="newParticipantLastName"
            type="text" 
            :label="t('participants.lastName')" 
            @validated="handleValidation" />
        </FormRow>
        <FormRow>
          <FormInput 
            :ref="addFormInputRef" 
            v-model="newParticipant.emailAddress"
            name="newParticipantEmailAddress" 
            type="text" 
            :label="t('participants.email')"
            @validated="handleValidation" />
          <FormInput 
            v-if="!isOnline"
            v-model="newParticipant.allergies"
            name="newParticipantAllergies" 
            type="text" 
            :rules="[]"
            :label="t('participants.allergies')"/>
        </FormRow>
        <FormRow>
          <FormCheckbox 
            v-if="!isOnline"
            v-model="newParticipant.reducedMobility"
            name="reducedMobility" 
            :label="t('participants.reducedMobility')"/>
        </FormRow>
        <button v-if="!showAddParticipantPlusButton" 
          class="btn btn--fullscreen" 
          type="submit">
          {{ t('global.add') }}</button>
      </Fieldset>
    </form>
  </div>
</template>

<script lang="ts" setup>
import FormRow from "@/components/forms/FormRow.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormInput from "@/components/forms/FormInput.vue";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";
import { ref, watch, defineEmits } from "vue";
import { useI18n } from "vue3-i18n";
import { notifyError, notifySuccess } from "@/notify";
import { useActivityOccurrenceService } from "@/inversify.config";
import { Status } from "@/validation";
import {Participant} from "@/types/entities/activity";
import {SucceededOrNotResponse} from "@/types/responses";

const { t } = useI18n();

const activityOccurrenceService = useActivityOccurrenceService();

// eslint-disable-next-line
interface propsInterface {
  isOnlineActivity: boolean,
  activityOccurrenceId: string,
}

// eslint-disable-next-line no-undef
const props = defineProps<propsInterface>();

const emit = defineEmits<{
  // states that the event has to be called 'update:modelValue'
  (event: "newParticipantAdded"): void;
}>();

const isOnline = ref<boolean>(props.isOnlineActivity);
const activityOccurrenceId = ref(props.activityOccurrenceId);

let newParticipant = ref<Participant>(new Participant);
const showAddParticipantPlusButton = ref<boolean>(true);

watch(props, (newProps) => {
  isOnline.value = newProps.isOnlineActivity;
  activityOccurrenceId.value = newProps.activityOccurrenceId;
});

watch(activityOccurrenceId, () => showAddParticipantPlusButton.value = true);

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses:any = {}

function addFormInputRef(ref: typeof FormInput) {
    if (!formInputs.value.includes(ref) && ref != null)
        formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
    inputValidationStatuses[name] = validationStatus.valid
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  }
  else {
    await addParticipant();
  }
}

async function addParticipant() {
  errorsMessages.value = []
  let response = await activityOccurrenceService.addParticipantToActivityOccurrence(activityOccurrenceId.value, newParticipant.value)
  if (!response.id) {
    notifyError(t('participants.saveError'))
    displayErrors(response as SucceededOrNotResponse)
    return
  }
  notifySuccess(t('participants.saveSuccess'))
  emit("newParticipantAdded")
  showAddParticipantPlusButton.value = !showAddParticipantPlusButton.value
  newParticipant.value = new Participant();
}

const errorsMessages = ref<string[]>([])
function displayErrors(response: SucceededOrNotResponse) {
  response = new SucceededOrNotResponse(response.succeeded, response.errors)
  if (response?.succeeded != undefined && !response?.succeeded) {
    errorsMessages.value = response.getErrorMessages('validation.participants')
  }
  else errorsMessages.value = []
}
</script>